import { Color, Mesh, Vector3, MeshBasicMaterial, Group } from 'three';

import { RoundedBoxGeometry } from 'three/examples/jsm/geometries/RoundedBoxGeometry';
import { TextGeometry } from 'three/examples/jsm/geometries/TextGeometry.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js';

import { mapRange } from '../../utils/helpers';
import { createHorizontalPhongGradientMaterial } from './gradientMaterialHelpers';

import {
  GRADIENT_ORANGE_PINK,
  GRADIENT_BLUE_PURPLE,
  GRADIENT_PINK_PURPLE,
  GRADIENT_GRAY,
} from '../constants';

import { BLOOM_RENDER_LAYER, BASE_RENDER_LAYER } from '../../threejs/constants';

import futuraFontRaw from '../../public/fonts/FuturaLTW05-Bold.typeface.json';

const PIE_GRADIENT_COLORS_WITHOUT_PIPELINE = {
  [GRADIENT_ORANGE_PINK]: {
    gradientColorTop: new Color(0xd1453d).multiplyScalar(2.1),
    gradientColorMiddle: new Color(0xf81c6f).multiplyScalar(2.1),
    gradientColorBottom: new Color(0xf81c6f).multiplyScalar(2.1),
  },
  [GRADIENT_BLUE_PURPLE]: {
    gradientColorTop: new Color(0x3a228b).multiplyScalar(2.1),
    gradientColorMiddle: new Color(0x3a228b).multiplyScalar(2.1),
    gradientColorBottom: new Color(0x0870c9).multiplyScalar(2.1),
  },
  [GRADIENT_PINK_PURPLE]: {
    gradientColorTop: new Color(0xde015d).multiplyScalar(2.1),
    gradientColorMiddle: new Color(0xde015d).multiplyScalar(2.1),
    gradientColorBottom: new Color(0x9b309b).multiplyScalar(2.1),
  },
  [GRADIENT_GRAY]: {
    gradientColorTop: new Color(0x383838).multiplyScalar(1).multiplyScalar(2.1),
    gradientColorMiddle: new Color(0x6b6a6a)
      .multiplyScalar(0.4)
      .multiplyScalar(2.1),
    gradientColorBottom: new Color(0x4e4e4e)
      .multiplyScalar(0.4)
      .multiplyScalar(2.1),
  },
};

const PIE_GRADIENT_COLORS_WITH_PIPELINE = {
  [GRADIENT_ORANGE_PINK]: {
    gradientColorTop: new Color(0xd1453d),
    gradientColorMiddle: new Color(0xf81c6f),
    gradientColorBottom: new Color(0xf81c6f),
  },
  [GRADIENT_BLUE_PURPLE]: {
    gradientColorTop: new Color(0x3a228b),
    gradientColorMiddle: new Color(0x3a228b),
    gradientColorBottom: new Color(0x0870c9),
  },
  [GRADIENT_PINK_PURPLE]: {
    gradientColorTop: new Color(0xde015d),
    gradientColorMiddle: new Color(0xde015d),
    gradientColorBottom: new Color(0x9b309b),
  },
  [GRADIENT_GRAY]: {
    gradientColorTop: new Color(0x383838).multiplyScalar(1),
    gradientColorMiddle: new Color(0x6b6a6a).multiplyScalar(0.4),
    gradientColorBottom: new Color(0x4e4e4e).multiplyScalar(0.4),
  },
};

const PIE_GRADIENT_COLORS = PIE_GRADIENT_COLORS_WITHOUT_PIPELINE;

const BAR_SETTINGS = {
  size: 8,
  segments: 1,
  radius: 0.45,
};

const parseRawPlotDataForBars = (rawPlotData = {}) => {
  const { yAxisMax, items } = rawPlotData;

  const foundYAxisMax = items
    .map((b) => Math.abs(b.value))
    .reduce((max, v) => Math.max(max, v), -Infinity);

  const parsedItems = items.map((item) => {
    return {
      ...item,
      value: item.value / (yAxisMax || foundYAxisMax),
    };
  });

  return {
    ...rawPlotData,
    items: parsedItems,
  };
};

const createVerticalBar = (pos, height, label, material) => {
  let barMesh = new Mesh(
    new RoundedBoxGeometry(
      BAR_SETTINGS.size,
      height,
      BAR_SETTINGS.size,
      BAR_SETTINGS.segments,
      BAR_SETTINGS.radius
    )
  );

  barMesh.material = material;
  barMesh.position.set(...pos);
  barMesh.name = `bar-${label}`;

  barMesh.layers.enable(BLOOM_RENDER_LAYER);

  return {
    mesh: barMesh,
    height,
  };
};

const createVerticalBars = (props) => {
  const {
    plotData,
    material,
    flippedMaterial,
    layout,
    minBarHeight,
    maxBarHeight,
  } = props;

  const barsData = parseRawPlotDataForBars(plotData);
  const barCount = barsData.items.length;

  let bars = barsData.items.map((b, i) => {
    const barWidthPadded = BAR_SETTINGS.size * 1.6;
    let barHeight = mapRange(
      Math.abs(b.value),
      0,
      1,
      minBarHeight,
      maxBarHeight
    );

    const barPosition = new Vector3(
      -((barCount - 1) * 0.5 * barWidthPadded) + barWidthPadded * i,
      0,
      0
    );

    const bar = createVerticalBar(
      barPosition,
      barHeight,
      `bar-${i}`,
      b.value > 0 ? material : flippedMaterial,
      layout
    );

    return {
      ...bar,
      ...b, // raw plot data
    };
  });

  const currMaxBarHeight = bars
    .filter((b) => b.value > 0)
    .reduce((max, b) => Math.max(max, b.height), -Infinity);
  const currNegMaxBarHeight = bars
    .filter((b) => b.value < 0)
    .reduce((min, b) => Math.min(min, -b.height), Infinity);
  const barsHaveNegativeValues = !!bars.find((b) => b.value < 0);

  bars = bars.map((b, i) => {
    const middleIndex = (bars.length + 1) / 2;
    const distToMiddleIndex = Math.abs(i + 1 - middleIndex);
    const perspectiveCorrection = 1 + (distToMiddleIndex / middleIndex) * 0.2;
    const xPerspectiveCorrection =
      (i === middleIndex
        ? 0
        : i < middleIndex - 2
        ? -perspectiveCorrection
        : i > middleIndex
        ? perspectiveCorrection
        : 0) * 1;

    return {
      // raw plot data
      ...b,
      // points used to align html labels to bars
      // we try to correct the perspective disalignment a bit
      bottomPosition: new Vector3(
        b.mesh.position.x + xPerspectiveCorrection,
        (-currMaxBarHeight * 0.5 +
          (barsHaveNegativeValues ? currNegMaxBarHeight * -0.5 : 0)) *
          (b.value > 0 ? 1.1 : 0.9),
        b.mesh.position.z
      ),
      topPosition: new Vector3(
        b.mesh.position.x + xPerspectiveCorrection,
        -currMaxBarHeight * 0.5 +
          (barsHaveNegativeValues ? currNegMaxBarHeight * -0.5 : 0) +
          (b.value > 0 ? b.height : -b.height * 1.04) +
          (b.value > 0 ? 1 : -1) * perspectiveCorrection,
        b.mesh.position.z
      ),
    };
  });

  return {
    bars,
    currMaxBarHeight,
    currNegMaxBarHeight,
    barsHaveNegativeValues,
  };
};

const parseRawPlotDataForPie = (items) => {
  const totalValueSum = items.reduce((sum, item) => {
    sum += item.value;
    return sum;
  }, 0);

  const parsedItems = items.map((item) => {
    return {
      ...item,
      value: item.value / totalValueSum,
    };
  });

  return {
    items: parsedItems,
  };
};

const createHorizontalBar = (width, label, material) => {
  let barMesh = new Mesh(
    new RoundedBoxGeometry(
      width,
      BAR_SETTINGS.size,
      BAR_SETTINGS.size,
      BAR_SETTINGS.segments,
      BAR_SETTINGS.radius
    )
  );

  barMesh.material = material;
  barMesh.position.set(0, 0, 0);
  barMesh.name = `bar-${label}`;

  barMesh.layers.enable(BLOOM_RENDER_LAYER);

  return {
    mesh: barMesh,
    width,
  };
};

const createPieBars = (props) => {
  const { items, totalWidth: _totalWidth, marginRight } = props;

  const futuraFont = new FontLoader().parse(futuraFontRaw);

  const totalWidth = _totalWidth - items.length * marginRight;
  const barsData = parseRawPlotDataForPie(items);

  let materials = {};
  let currentTotalW = 0;

  let bars = barsData.items.map((b, i) => {
    const barWidth = b.value * totalWidth;
    const barColor = b.barColor || GRADIENT_GRAY;

    let material;
    if (b.barColor in materials) {
      material = materials[barColor];
    } else {
      material = materials[barColor] = createHorizontalPhongGradientMaterial({
        gradientColors:
          barColor in PIE_GRADIENT_COLORS
            ? PIE_GRADIENT_COLORS[barColor]
            : PIE_GRADIENT_COLORS[GRADIENT_GRAY],
        envMap: null,
        pointerLightIntensity: 1,
        normedGradientMiddle: 0.2,
        // noise texture
        noiseAmount: 0.04,
      });
    }

    const bar = createHorizontalBar(
      barWidth,
      `bar-${i}`,
      material.glassMaterial
    );

    const fontSize = 2.8;
    const textGeometry = new TextGeometry(`${parseInt(b.value * 100)}%`, {
      font: futuraFont,
      size: fontSize,
      height: 0.2,
      bevelEnabled: false,
    });

    const textMaterial = new MeshBasicMaterial({
      color: b.labelColor || 0xffffff,
    });

    const textMesh = new Mesh(textGeometry, textMaterial);
    textMesh.layers.enable(BASE_RENDER_LAYER);

    let textXPos = -barWidth / 2 + 2;
    textMesh.position.set(textXPos, -fontSize / 2, 4.3);

    const barGroup = new Group();

    barGroup.add(bar.mesh);
    barGroup.add(textMesh);

    const leftX = -0.5 * totalWidth + currentTotalW;
    const perspectiveCorrectionLeft = (leftX / totalWidth) * 8;

    const leftPosition = new Vector3(
      leftX + perspectiveCorrectionLeft,
      -BAR_SETTINGS.size,
      0
    );

    const rightX = -0.5 * totalWidth + currentTotalW + barWidth;
    const perspectiveCorrectionRight = (rightX / totalWidth) * 8;

    const rightPosition = new Vector3(
      rightX + perspectiveCorrectionRight,
      -BAR_SETTINGS.size,
      0
    );

    const d = {
      ...bar,
      ...b,
      mesh: barGroup,
      currentTotalW,
      leftPosition,
      rightPosition,
    };

    currentTotalW += barWidth + marginRight;

    return d;
  });

  return {
    bars,
    barMaterials: materials,
  };
};

const scaleBarY = (bar, scale, currMaxBarHeight) => {
  const mesh = bar.mesh;
  const height = bar.height;

  mesh.scale.y = scale;

  if (bar.value > 0) {
    mesh.position.y = (height * scale) / 2 - currMaxBarHeight * 0.5;
  } else {
    mesh.position.y = (-height * scale) / 2 - currMaxBarHeight * 0.5;
  }
};

const scaleBarX = (bar, scale, totalBarsWidth) => {
  const mesh = bar.mesh;
  const width = bar.width;

  mesh.scale.x = scale;

  mesh.position.x =
    -0.5 * totalBarsWidth + bar.currentTotalW + 0.5 * width * scale;
};

export { createVerticalBars, createPieBars, scaleBarY, scaleBarX };
